/** Rules for which tasks can be scheduled or shown as calendar blocks. */

const isOpenTask = (task) =>
  Boolean(task) && !task.completed && task.status !== 'Backlog';

/**
 * @param {import('@/types').Task} task
 * @returns {string|null}
 */
const getDescendantPathPrefix = (task) => {
  if (!task?.materializedPath) return null;
  const path = task.materializedPath;
  return path.endsWith('.') ? path : `${path}.`;
};

/**
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {boolean}
 */
export function isSchedulingBlocked(task, tasksMap) {
  if (!task?.id || !tasksMap?.size) return false;

  const pathPrefix = getDescendantPathPrefix(task);

  for (const candidate of tasksMap.values()) {
    if (candidate.id === task.id || !isOpenTask(candidate)) continue;

    if (candidate.parentId === task.id) return true;

    if (pathPrefix && candidate.materializedPath?.startsWith(pathPrefix)) {
      return true;
    }
  }

  return false;
}

/**
 * Schedulable when active, incomplete, and has no open subtasks.
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {boolean}
 */
export function isSchedulableTask(task, tasksMap) {
  if (!task || task.completed || task.status === 'Backlog') return false;
  return !isSchedulingBlocked(task, tasksMap);
}

/**
 * Day-grid visibility: same as schedulable, but completed blocks stay on the calendar.
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {boolean}
 */
export function isDayGridTask(task, tasksMap) {
  if (!task || task.status === 'Backlog') return false;
  return !isSchedulingBlocked(task, tasksMap);
}

/**
 * Parent IDs that have at least one task in the day set (direct or nested child).
 * Used to avoid counting rollup parent duration when subtasks are counted separately.
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {Set<string>}
 */
export function getParentIdsWithCountedDescendants(tasksForDay, tasksMap) {
  const parentIds = new Set();
  if (!tasksForDay?.length || !tasksMap?.size) return parentIds;

  for (const task of tasksForDay) {
    const mappedTask = tasksMap.get(task.id) ?? task;
    let currentId = mappedTask?.parentId ?? task?.parentId ?? null;
    while (currentId) {
      parentIds.add(currentId);
      const parent = tasksMap.get(currentId);
      currentId = parent?.parentId ?? null;
    }
  }

  return parentIds;
}

/**
 * True when the task is a work unit for the day (not a parent whose descendants are also on that day).
 * @param {import('@/types').Task} task
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {boolean}
 */
export function isWorkUnitForDay(task, tasksForDay, tasksMap) {
  if (!task?.id) return false;
  return !getParentIdsWithCountedDescendants(tasksForDay, tasksMap).has(
    task.id,
  );
}

/**
 * Drops rollup/container parents when their descendants are scheduled on the same day.
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {import('@/types').Task[]}
 */
export function filterDayWorkUnits(tasksForDay, tasksMap) {
  if (!tasksForDay?.length) return [];
  const parentIds = getParentIdsWithCountedDescendants(tasksForDay, tasksMap);
  return tasksForDay.filter((task) => !parentIds.has(task.id));
}

/**
 * Minutes that count toward day capacity. Blocked/rollup parents contribute 0 when
 * their descendants are in the same day set (work lives in subtasks).
 * @param {import('@/types').Task} task
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {number}
 */
export function getCapacityDurationMinutes(task, tasksForDay, tasksMap) {
  if (!task || isSchedulingBlocked(task, tasksMap)) return 0;
  if (!isWorkUnitForDay(task, tasksForDay, tasksMap)) return 0;
  return task.duration || 0;
}

/**
 * Day capacity snapshot — DayProgressDots rules.
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {{
 *   countingTasks: import('@/types').Task[],
 *   plannedMins: number,
 *   completedMins: number,
 *   completedTasks: import('@/types').Task[],
 * }}
 */
export function getDayCapacitySnapshot(tasksForDay, tasksMap) {
  const map = tasksMap ?? new Map();
  const dayTasks = (tasksForDay ?? []).filter((task) => !task.archived);
  const countingTasks = dayTasks.filter((task) => isDayGridTask(task, map));
  const capacityMinutes = (task) =>
    getCapacityDurationMinutes(task, countingTasks, map);
  const plannedMins = countingTasks.reduce(
    (sum, task) => sum + capacityMinutes(task),
    0,
  );
  const completedTasks = countingTasks.filter((task) => task.completed);
  const completedMins = completedTasks.reduce(
    (sum, task) => sum + capacityMinutes(task),
    0,
  );
  return { countingTasks, plannedMins, completedMins, completedTasks };
}

/**
 * Planned and completed capacity minutes for one calendar day — DayProgressDots rules.
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {{ plannedMins: number, completedMins: number }}
 */
export function getDayCapacityMinutes(tasksForDay, tasksMap) {
  const { plannedMins, completedMins } = getDayCapacitySnapshot(
    tasksForDay,
    tasksMap,
  );
  return { plannedMins, completedMins };
}

/**
 * Planned capacity minutes for one calendar day — same rules as DayProgressDots.
 * @param {import('@/types').Task[]} tasksForDay
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {number}
 */
export function getDayPlannedCapacityMinutes(tasksForDay, tasksMap) {
  return getDayCapacityMinutes(tasksForDay, tasksMap).plannedMins;
}

/**
 * @param {import('@/types').Task[]} tasks
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {import('@/types').Task[]}
 */
export function filterSchedulableTasks(tasks, tasksMap) {
  if (!tasks?.length) return [];
  return tasks.filter((task) => isSchedulableTask(task, tasksMap));
}

/**
 * True when an update assigns a calendar date or time (not clearing them).
 * @param {import('@/types').UpdateTask} details
 * @returns {boolean}
 */
export function isAssigningSchedule(details) {
  if (
    details.date !== undefined &&
    details.date !== null &&
    details.date !== ''
  ) {
    return true;
  }
  if (
    details.time !== undefined &&
    details.time !== null &&
    details.time !== ''
  ) {
    return true;
  }
  return false;
}

/**
 * @param {import('@/types').Task[]} allTasks
 * @returns {Map<string, import('@/types').Task>}
 */
export function buildTasksMap(allTasks) {
  return new Map((allTasks ?? []).map((task) => [task.id, task]));
}

/**
 * Throws when a blocked parent/container task receives a time assignment.
 * Date-only assignment (deadline semantics, no work-day/capacity meaning) is always
 * allowed, even on blocked tasks. Completion-driven date/time adjustment is allowed.
 * @param {import('@/types').Task | undefined} originalTask
 * @param {import('@/types').UpdateTask} details
 * @param {import('@/types').Task[]} allTasks
 */
export function assertSchedulableScheduleUpdate(
  originalTask,
  details,
  allTasks,
) {
  if (!originalTask) return;

  const assigningTime =
    details.time !== undefined && details.time !== null && details.time !== '';
  if (!assigningTime) return;

  if (details.completed === true && !originalTask.completed) return;

  const tasksMap = buildTasksMap(allTasks);
  if (isSchedulingBlocked(originalTask, tasksMap)) {
    throw new Error('SCHEDULING_BLOCKED');
  }
}
